/**
 * 🟥 CUADRO NEGRO — MainLayout con Device Simulator REAL (2026-07-06 v2)
 *
 * MODO DESKTOP: renderiza el layout normal (header, rutas, footer).
 * MODO TABLET / CELL: reemplaza el contenido con un iframe REAL del
 * mismo path + `?device_iframe=1`, en un ancho fijo (768 o 375). El
 * iframe ejecuta la app entera con `window.innerWidth = 768/375`, lo
 * que hace que TODOS los media queries de Tailwind respondan a un
 * viewport real de tablet/cell — el layout responsive se ve como en
 * un dispositivo real.
 *
 * Además renderizamos un mini-toolbar flotante con el DeviceSwitchButton
 * para poder ciclar sin perder acceso al control.
 *
 * Dentro del iframe MainLayout detecta el flag `device_iframe=1` y
 * devuelve los children puros (evita recursión y muestra la app tal
 * cual sería en el dispositivo).
 */
import { useLocation } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { useGlobalUI } from '../context/GlobalUIContext';
import DeviceSwitchButton from '../components/DeviceSwitchButton';

const WIDTHS = { desktop: 1280, tablet: 768, cell: 375 };

const MainLayout = ({ children }) => {
  const { deviceMode, setDeviceMode } = useGlobalUI();
  const location = useLocation();

  const isInsideSimIframe =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('device_iframe') === '1';

  // Dentro del iframe: children puros. La app cree que el viewport
  // es el ancho del iframe y los media queries Tailwind reaccionan
  // correctamente.
  if (isInsideSimIframe) {
    return <>{children}</>;
  }

  // Modo Desktop: layout normal.
  if (deviceMode === 'desktop') {
    return (
      <div
        className="device-container device-desktop"
        data-testid="device-container"
        data-device-mode="desktop"
      >
        <div key="desktop">{children}</div>
      </div>
    );
  }

  // Modo Tablet / Cell: iframe REAL con viewport real.
  const width = WIDTHS[deviceMode] || WIDTHS.desktop;
  const path = location.pathname + location.search;
  const sep = path.includes('?') ? '&' : '?';
  const iframeSrc = `${path}${sep}device_iframe=1`;

  return (
    <div
      className={`device-container device-${deviceMode}`}
      data-testid="device-container"
      data-device-mode={deviceMode}
      style={{
        background: '#020617',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '52px 8px 12px 8px',
        position: 'relative',
      }}
    >
      {/* Toolbar flotante SIEMPRE visible con el DeviceSwitchButton
          para poder ciclar / volver a Desktop en modo simulación. */}
      <div
        data-testid="device-sim-toolbar"
        style={{
          position: 'fixed',
          top: 8,
          left: 0,
          right: 0,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          background: 'rgba(2, 6, 23, 0.85)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #1e293b',
          zIndex: 99999,
        }}
      >
        <span style={{ color: '#94a3b8', fontSize: 12, fontFamily: 'system-ui' }}>
          {deviceMode === 'tablet' ? 'Tablet · 768 × 100vh' : 'Cell · 375 × 100vh'}
        </span>
        <DeviceSwitchButton />
        <button
          type="button"
          onClick={() => setDeviceMode('desktop')}
          data-testid="device-sim-exit-btn"
          title="Salir de simulación"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            fontSize: 12,
            color: '#e2e8f0',
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          <Monitor style={{ width: 14, height: 14 }} />
          Volver a Desktop
        </button>
      </div>

      <iframe
        key={deviceMode}
        title={`DamyxIA — ${deviceMode} preview`}
        src={iframeSrc}
        data-testid="device-simulator-iframe"
        style={{
          width: `${width}px`,
          height: 'calc(100vh - 68px)',
          border: '1px solid #334155',
          borderRadius: 12,
          background: '#0f172a',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
        }}
      />
    </div>
  );
};

export default MainLayout;
